/* eslint no-use-before-define: ["error", { "variables": false }] */

import PropTypes from 'prop-types';
import React from 'react';

import { Text, Linking, StyleSheet, View } from 'react-native-web';

import MessageText from './MessageText';
import MessageImage from './MessageImage';
import MessageVideo from './MessageVideo';

import Time from './Time';
import Color from './Color';
import DeleteHoverIcon from '../img/icon-Delete2-mouse-over.png';
import './Bubble.css';

import { isSameUser, isSameDay } from './utils';
import TouchableOpacity from './TouchableOpacity';
import { formatQuoteText } from '../utils/messageParser';

export default class Bubble extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      maxWidth: 'auto',
      minWidth: 'auto',
      chatPageWidth: 0,
      onHover: false,
      sharedWidth: null,
      hoveredButtonIndex: null
    };
    this.mainBubbleElement = null;
    this.quoteBubbleElement = null;
    this.timeContainerElement = null;
    this.actionContainerElement = null;
    this.resizeObserver = null;
    this.enclosingDiv = null;
  }

  updateWidth = () => {
    const enclosingDiv = this.enclosingDiv || document.getElementById('idEnclosingParent');
    if (!enclosingDiv) {
      return;
    }
    const chatPageWidth = enclosingDiv.offsetWidth;
    const leftMaxWidth = chatPageWidth * 0.7;
    const rightMaxWidth = chatPageWidth * 0.78;
    const available = this.props.position === 'left' ? leftMaxWidth : rightMaxWidth;
    // Never force 300px on narrow panels — that overflows the chat and shows a horizontal bar.
    const minWidthValue = Math.min(300, Math.max(0, available));

    this.setState(
      {
        maxWidth: available + 'px',
        minWidth: minWidthValue + 'px',
        chatPageWidth: chatPageWidth,
        leftMaxWidth: leftMaxWidth + 'px',
        rightMaxWidth: rightMaxWidth + 'px'
      },
      () => {
        requestAnimationFrame(() => {
          setTimeout(() => this.measureAndSyncBubbleWidths(), 50);
        });
      }
    );
  };

  componentDidMount() {
    const enclosingDiv = document.getElementById('idEnclosingParent');
    if (!enclosingDiv) {
      return;
    }
    this.enclosingDiv = enclosingDiv;

    this.updateWidth();

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateWidth();
      });
      this.resizeObserver.observe(enclosingDiv);
    } else {
      window.addEventListener('resize', this.updateWidth);
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.updateWidth);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.currentMessage.quote !== this.props.currentMessage.quote ||
      prevState.chatPageWidth !== this.state.chatPageWidth
    ) {
      requestAnimationFrame(() => {
        setTimeout(() => this.measureAndSyncBubbleWidths(), 50);
      });
    }
  }

  measureAndSyncBubbleWidths = () => {
    const quote = this.props.currentMessage.quote;
    const showQuote = typeof quote === 'string' && quote.length > 0;

    if (!showQuote) {
      if (this.state.sharedWidth !== null) {
        this.setState({ sharedWidth: null });
      }
      return;
    }

    if (this.mainBubbleElement && this.quoteBubbleElement) {
      const mainWidth = this.mainBubbleElement.offsetWidth || this.mainBubbleElement.clientWidth;
      const quoteWidth = this.quoteBubbleElement.offsetWidth || this.quoteBubbleElement.clientWidth;

      if (mainWidth > 0 && quoteWidth > 0) {
        const maxWidthValue =
          this.props.position === 'left' ? this.state.chatPageWidth * 0.7 : this.state.chatPageWidth * 0.78;
        const minWidthValue = Math.min(300, Math.max(0, maxWidthValue));

        const baseWidth = quoteWidth;
        const constrainedWidth = Math.max(minWidthValue, Math.min(baseWidth, maxWidthValue));

        const newSharedWidth = constrainedWidth + 'px';
        if (this.state.sharedWidth !== newSharedWidth) {
          this.setState({ sharedWidth: newSharedWidth });
        }
      }
    }
  };

  setMainBubbleRef = (element) => {
    this.mainBubbleElement = element;
    if (element) {
      requestAnimationFrame(() => {
        setTimeout(() => this.measureAndSyncBubbleWidths(), 50);
      });
    }
  };

  setQuoteBubbleRef = (element) => {
    this.quoteBubbleElement = element;
    if (element) {
      requestAnimationFrame(() => {
        setTimeout(() => this.measureAndSyncBubbleWidths(), 50);
      });
    }
  };

  onPress = () => {
    if (this.props.onPress) {
      this.props.onPress(this.props.currentMessage);
    } else if (this.props.currentMessage.attachment) {
      const url = this.props.currentMessage.attachment.url;
      if (url) {
        Linking.canOpenURL(url).then((supported) => {
          if (!supported) {
            // eslint-disable-next-line
          } else {
            Linking.openURL(url);
          }
        });
      }
    }
    this.setState({ onHover: false });
  };

  onHoverIn = () => {
    this.setState({ onHover: true });
  };
  onHoverOut = () => {
    this.setState({ onHover: false });
  };
  onLongPress = () => {
    if (this.props.onLongPress) {
      this.props.onLongPress(this.props.currentMessage);
    } else if (this.props.currentMessage.text) {
      this.setState({ onHover: true });
    }
  };

  handleBubbleToNext() {
    if (
      isSameUser(this.props.currentMessage, this.props.nextMessage) &&
      isSameDay(this.props.currentMessage, this.props.nextMessage)
    ) {
      return StyleSheet.flatten([
        styles[this.props.position].containerToNext,
        this.props.containerToNextStyle[this.props.position]
      ]);
    }
    return null;
  }

  handleBubbleToPrevious() {
    if (
      isSameUser(this.props.currentMessage, this.props.previousMessage) &&
      isSameDay(this.props.currentMessage, this.props.previousMessage)
    ) {
      return StyleSheet.flatten([
        styles[this.props.position].containerToPrevious,
        this.props.containerToPreviousStyle[this.props.position]
      ]);
    }
    return null;
  }

  renderMessageAction() {
    const { messageActionSheet } = this.props;
    if (!this.state.onHover || !messageActionSheet) {
      return null;
    }

    const actions = messageActionSheet(this.props.currentMessage);
    if (!actions || !Array.isArray(actions)) {
      return null;
    }

    return actions.map((action, index) => {
      const iconStr = action.icon;
      const isImageIcon =
        typeof iconStr === 'string' &&
        (iconStr.startsWith('http://') ||
          iconStr.startsWith('https://') ||
          iconStr.startsWith('/') ||
          iconStr.includes('/') ||
          iconStr.includes('\\') ||
          iconStr.match(/\.(png|jpg|jpeg|gif|svg|webp|ico|bmp|webp)$/i));

      const isCharacterIcon =
        typeof iconStr === 'string' && !isImageIcon && iconStr.length < 10 && !iconStr.match(/^[a-zA-Z0-9_.-]+$/);

      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (action.disabled) {
          return;
        }
        if (action.action) {
          action.action(this.props.currentMessage);
        }
        this.setState({ onHover: false, hoveredButtonIndex: null });
      };

      const isDisabled = action.disabled === true;
      const handleMouseEnter = () => {
        if (!isDisabled) {
          this.setState({ hoveredButtonIndex: index });
        }
      };

      const handleMouseLeave = () => {
        this.setState({ hoveredButtonIndex: null });
      };

      const isHovered = !isDisabled && this.state.hoveredButtonIndex === index;

      const actionType = action.type || '';
      const isDeleteAction = actionType === 'delete';
      const isRemoveMyCollectionAction = actionType === 'removeMyCollection';
      const isRemoveGroupCollectionAction = actionType === 'removeGroupCollection';
      const isCollectionAction = actionType === 'myCollection' || actionType === 'groupCollection';
      const isRemoveCollectionAction = isRemoveMyCollectionAction || isRemoveGroupCollectionAction;
      const marginRight = isRemoveCollectionAction ? 9 : isCollectionAction ? 3 : -3;
      const marginLeft = isRemoveCollectionAction ? 2 : 0;
      const dynamicStyles = {
        marginRight: marginRight,
        marginLeft: marginLeft,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1
      };

      if (isCharacterIcon) {
        return (
          <span
            key={action.label}
            className='message-action-button'
            style={dynamicStyles}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <span
              style={{
                fontSize: '18px',
                lineHeight: '18px',
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                backgroundColor: isHovered ? '#000' : 'transparent',
                borderRadius: isHovered ? '50%' : '0',
                filter: isHovered ? 'invert(1)' : 'none',
                position: 'relative'
              }}
            >
              {action.icon}
            </span>
            <span
              style={{
                fontSize: '8px',
                fontWeight: 600,
                color: Color.text,
                whiteSpace: 'nowrap',
                position: 'relative',
                opacity: isDisabled ? 0.5 : 1
              }}
            >
              {action.label}
            </span>
          </span>
        );
      }

      if (isDeleteAction) {
        return (
          <span
            key={action.label}
            className='message-action-button'
            style={dynamicStyles}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <span
              style={{
                backgroundColor: isHovered ? '#b71c1c' : '#d32f2f',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                marginBottom: 2,
                position: 'relative'
              }}
            >
              <img
                style={{
                  width: '18px',
                  height: '18px',
                  display: 'block'
                }}
                src={isHovered ? DeleteHoverIcon : action.icon}
                alt={action.label}
              />
            </span>
            <span
              style={{
                fontSize: '8px',
                fontWeight: 600,
                color: Color.text,
                whiteSpace: 'nowrap',
                position: 'relative',
                opacity: isDisabled ? 0.5 : 1
              }}
            >
              {action.label}
            </span>
          </span>
        );
      }

      return (
        <span
          key={action.label}
          className='message-action-button'
          style={dynamicStyles}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span
            style={{
              width: '18px',
              height: '18px',
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isHovered ? '#000' : 'transparent',
              borderRadius: isHovered ? '50%' : '0',
              position: 'relative'
            }}
          >
            <img
              className='idigest-chat-toolbar-icon'
              style={{
                width: '18px',
                height: '18px',
                display: 'block',
                filter: isHovered ? 'invert(1)' : 'var(--idigest-chat-toolbar-icon-filter, none)'
              }}
              src={action.icon}
              alt={action.label}
            />
          </span>
          <span
            style={{
              fontSize: '8px',
              fontWeight: 600,
              color: Color.text,
              whiteSpace: 'nowrap',
              position: 'relative',
              opacity: isDisabled ? 0.5 : 1
            }}
          >
            {action.label}
          </span>
        </span>
      );
    });
  }

  renderMessageText() {
    if (this.props.renderCustomView) {
      const customView = this.props.renderCustomView(this.props.currentMessage);
      if (customView) {
        return null;
      }
    }
    if (this.props.currentMessage.text) {
      //eslint-disable-next-line
      const { containerStyle, wrapperStyle, ...messageTextProps } = this.props;
      if (this.props.renderMessageText) {
        return this.props.renderMessageText(messageTextProps);
      }
      return <MessageText {...messageTextProps} />;
    }
    return null;
  }

  renderMessageImage() {
    if (this.props.currentMessage.image) {
      //eslint-disable-next-line
      const { containerStyle, wrapperStyle, ...messageImageProps } = this.props;
      if (this.props.renderMessageImage) {
        return this.props.renderMessageImage(messageImageProps);
      }
      return <MessageImage {...messageImageProps} />;
    }
    return null;
  }

  renderMessageVideo() {
    if (this.props.currentMessage.video) {
      //eslint-disable-next-line
      const { containerStyle, wrapperStyle, ...messageVideoProps } = this.props;
      if (this.props.renderMessageVideo) {
        return this.props.renderMessageVideo(messageVideoProps);
      }
      return <MessageVideo {...messageVideoProps} />;
    }
    return null;
  }

  renderTicks() {
    const { currentMessage } = this.props;
    if (this.props.renderTicks) {
      return this.props.renderTicks(currentMessage);
    }
    if (currentMessage.user.id !== this.props.user.id) {
      return;
    }
    if (currentMessage.sent || currentMessage.received || currentMessage.pending) {
      return (
        <View style={styles.tickView}>
          {currentMessage.sent && <Text style={[styles.tick, this.props.tickStyle]}>✓</Text>}
          {currentMessage.received && <Text style={[styles.tick, this.props.tickStyle]}>✓</Text>}
          {currentMessage.pending && <Text style={[styles.tick, this.props.tickStyle]}>🕓</Text>}
        </View>
      );
    }
    return null;
  }

  renderTime() {
    if (this.props.hideFooterMetadata) {
      return null;
    }
    if (this.props.currentMessage.createdAt) {
      //eslint-disable-next-line
      const { containerStyle, wrapperStyle, ...timeProps } = this.props;
      const hasActions = this.state.onHover && this.props.messageActionSheet;
      const timeStyle = hasActions
        ? {
            alignSelf: 'center',
            marginTop: '-10px'
          }
        : {
            alignSelf: 'flex-start'
          };
      if (this.props.renderTime) {
        return <View style={timeStyle}>{this.props.renderTime(timeProps)}</View>;
      }
      return (
        <View style={timeStyle}>
          <Time {...timeProps} />
        </View>
      );
    }
    return null;
  }

  renderUsername() {
    if (this.props.hideFooterMetadata) {
      return null;
    }
    const { currentMessage } = this.props;
    if (this.props.renderUsernameOnMessage) {
      if (currentMessage.user.id === this.props.user.id) {
        return null;
      }
      return (
        <View style={styles.usernameView}>
          <Text style={[styles.username, this.props.usernameStyle]}>~ {currentMessage.user.name}</Text>
        </View>
      );
    }
    return null;
  }

  renderCustomView() {
    const { currentMessage } = this.props;
    if (this.props.renderCustomView) {
      return this.props.renderCustomView(currentMessage);
    }
    return null;
  }

  render() {
    const quote = this.props.currentMessage.quote;
    const showQuote = typeof quote === 'string' && quote.length > 0;
    const isAudioMessage = !!this.props.currentMessage.audio;
    const isMergedForward = this.props.currentMessage.type === 'mergedForward';

    if (isMergedForward && this.props.renderBubble) {
      const customBubble = this.props.renderBubble(this.props);
      if (customBubble !== null && customBubble !== undefined) {
        return customBubble;
      }
    }

    let bottomStyle = {};
    if (showQuote) {
      bottomStyle = {
        borderBottomLeftRadius: showQuote ? 0 : styles.borderBottomLeftRadius,
        borderBottomRightRadius: showQuote ? 0 : styles.borderBottomRightRadius
      };
    }

    const audioButtonStyle = isAudioMessage
      ? {
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'opacity 0.2s ease'
        }
      : {};

    if (isMergedForward) {
      const mergedForwardWidth = '300px';
      const mergedForwardStyle = {
        width: mergedForwardWidth,
        maxWidth: mergedForwardWidth,
        minWidth: mergedForwardWidth
      };
      return (
        <View style={[styles[this.props.position].container, this.props.containerStyle[this.props.position]]}>
          <View ref={this.setMainBubbleRef}>
            <View
              onMouseEnter={this.onHoverIn}
              onMouseLeave={this.onHoverOut}
              style={[
                styles[this.props.position].wrapper,
                this.props.wrapperStyle[this.props.position],
                this.handleBubbleToNext(),
                this.handleBubbleToPrevious(),
                bottomStyle,
                audioButtonStyle,
                mergedForwardStyle,
                { cursor: 'pointer' }
              ]}
            >
              <TouchableOpacity
                style={{
                  ...audioButtonStyle
                }}
                withoutFeedback
                onLongPress={this.onLongPress}
                onPress={this.onPress}
                accessibilityTraits='text'
                {...this.props.touchableProps}
              >
                <View>
                  {this.renderCustomView()}
                  {this.renderMessageImage()}
                  {this.renderMessageVideo()}
                  {this.renderMessageText()}
                  <View
                    style={[styles[this.props.position].bottom, this.props.bottomContainerStyle[this.props.position]]}
                  >
                    <View style={styles[this.props.position].timeContainer}>
                      {this.renderUsername()}
                      {this.renderTime()}
                      {this.renderTicks()}
                    </View>
                    <View
                      style={[
                        styles[this.props.position].actionContainer,
                        {
                          minWidth: this.props.messageActionSheet ? '180px' : 0,
                          height: this.props.messageActionSheet ? '30px' : 0,
                          paddingTop: this.props.messageActionSheet ? 2 : 0,
                          opacity: this.state.onHover ? 1 : 0,
                          pointerEvents: this.state.onHover ? 'auto' : 'none',
                          transition: 'opacity 0.2s ease-in-out',
                          visibility: this.state.onHover ? 'visible' : 'hidden',
                          overflow: 'hidden'
                        }
                      ]}
                    >
                      {this.renderMessageAction()}
                    </View>
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      );
    }

    const maxWidthValue =
      this.state.chatPageWidth > 0
        ? this.props.position === 'left'
          ? `${this.state.chatPageWidth * 0.7}px`
          : `${this.state.chatPageWidth * 0.78}px`
        : undefined;
    const maxWidth = (showQuote && this.state.sharedWidth) || maxWidthValue;
    // Do not fall back to a fixed 300px — narrow chat panels cannot fit it.
    const minWidth = this.state.minWidth !== 'auto' ? this.state.minWidth : undefined;
    const sharedWidthStyle = {};
    if (showQuote && this.state.sharedWidth) {
      sharedWidthStyle.width = this.state.sharedWidth;
      sharedWidthStyle.maxWidth = this.state.sharedWidth;
      sharedWidthStyle.minWidth = this.state.sharedWidth;
    } else {
      if (maxWidth) {
        sharedWidthStyle.maxWidth = maxWidth;
      }
      if (minWidth) {
        sharedWidthStyle.minWidth = minWidth;
      }
    }

    const isSameUserAsPrevious =
      this.props.position === 'right'
        ? isSameUser(this.props.currentMessage, this.props.previousMessage) &&
          isSameDay(this.props.currentMessage, this.props.previousMessage)
        : false;
    const isSameUserAsNext =
      this.props.position === 'right'
        ? isSameUser(this.props.currentMessage, this.props.nextMessage) &&
          isSameDay(this.props.currentMessage, this.props.nextMessage)
        : false;
    const isConsecutiveRightMessage = isSameUserAsPrevious || isSameUserAsNext;
    const marginTopStyle = this.props.position === 'right' && isConsecutiveRightMessage ? { marginTop: 2 } : {};

    return (
      <View style={[styles[this.props.position].container, this.props.containerStyle[this.props.position]]}>
        <View ref={this.setMainBubbleRef}>
          <View
            onMouseEnter={this.onHoverIn}
            onMouseLeave={this.onHoverOut}
            style={[
              styles[this.props.position].wrapper,
              this.props.wrapperStyle[this.props.position],
              this.handleBubbleToNext(),
              this.handleBubbleToPrevious(),
              bottomStyle,
              audioButtonStyle,
              sharedWidthStyle,
              marginTopStyle,
              { cursor: 'pointer' }
            ]}
          >
            <TouchableOpacity
              style={{
                ...audioButtonStyle
              }}
              withoutFeedback
              onLongPress={this.onLongPress}
              onPress={this.onPress}
              accessibilityTraits='text'
              {...this.props.touchableProps}
            >
              <View>
                {this.renderCustomView()}
                {this.renderMessageImage()}
                {this.renderMessageVideo()}
                {this.renderMessageText()}
                <View
                  style={[styles[this.props.position].bottom, this.props.bottomContainerStyle[this.props.position]]}
                >
                  <View style={styles[this.props.position].timeContainer}>
                    {this.renderUsername()}
                    {this.renderTime()}
                    {this.renderTicks()}
                  </View>
                  <View
                    style={[
                      styles[this.props.position].actionContainer,
                      {
                        minWidth: this.props.messageActionSheet ? '180px' : 0,
                        height: this.props.messageActionSheet ? '30px' : 0,
                        paddingTop: this.props.messageActionSheet ? 2 : 0,
                        opacity: this.state.onHover ? 1 : 0,
                        pointerEvents: this.state.onHover ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease-in-out',
                        visibility: this.state.onHover ? 'visible' : 'hidden',
                        overflow: 'hidden'
                      }
                    ]}
                  >
                    {this.renderMessageAction()}
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </View>
        <View>
          {showQuote ? (
            <TouchableOpacity
              activeOpacity={1}
              onPress={() => {
                if (typeof this.props.onQuotePress === 'function') {
                  this.props.onQuotePress(this.props.currentMessage);
                }
              }}
            >
              <View
                ref={this.setQuoteBubbleRef}
                data-testid='quote-bubble'
                style={[
                  styles[this.props.position].wrapper,
                  this.props.wrapperStyle[this.props.position],
                  this.handleBubbleToNext(),
                  this.handleBubbleToPrevious(),
                  {
                    marginTop: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    backgroundColor: Color.quoteBg,
                    borderWidth: 0,
                    cursor: 'pointer',
                    userSelect: 'none'
                  },
                  sharedWidthStyle
                ]}
              >
                <Text
                  style={{
                    paddingTop: 5,
                    paddingBottom: 5,
                    paddingLeft: 15,
                    paddingRight: 15,
                    color: Color.white,
                    maxWidth: showQuote && this.state.sharedWidth ? this.state.sharedWidth : this.state.maxWidth,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                  numberOfLines={2}
                  ellipsizeMode='tail'
                >
                  {formatQuoteText(quote).replace(/\n/g, ' ')}
                </Text>
              </View>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  }
}

const styles = {
  left: StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'flex-start'
    },
    wrapper: {
      borderRadius: 15,
      backgroundColor: Color.leftBubbleBackground,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: Color.bubbleBorder,
      marginRight: 60,
      marginTop: 0,
      paddingTop: 6,
      minHeight: 20,
      justifyContent: 'flex-end',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      position: 'relative',
      width: 'auto',
      flexShrink: 1
    },
    containerToNext: {
      borderBottomLeftRadius: 3
    },
    containerToPrevious: {
      borderTopLeftRadius: 3
    },
    bottom: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      paddingRight: 4,
      paddingLeft: 8,
      paddingBottom: 2,
      marginTop: 2,
      position: 'relative',
      minHeight: 32,
      height: 'auto'
    },
    timeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1
    },
    actionContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      zIndex: 10,
      marginLeft: 5
    }
  }),
  right: StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'flex-end'
    },
    wrapper: {
      borderRadius: 15,
      backgroundColor: Color.rightBubbleBackground,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: Color.bubbleBorder,
      marginLeft: 60,
      marginTop: 18,
      paddingTop: 6,
      minHeight: 20,
      justifyContent: 'flex-end',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      position: 'relative',
      width: 'auto',
      flexShrink: 1
    },
    containerToNext: {
      borderBottomRightRadius: 3
    },
    containerToPrevious: {
      borderTopRightRadius: 3
    },
    bottom: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      paddingRight: 4,
      paddingLeft: 8,
      paddingBottom: 2,
      marginTop: 2,
      position: 'relative',
      minHeight: 32,
      height: 'auto'
    },
    timeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1
    },
    actionContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      zIndex: 10,
      marginLeft: 5
    }
  }),
  tick: {
    fontSize: 10,
    backgroundColor: Color.backgroundTransparent,
    color: Color.white
  },
  tickView: {
    flexDirection: 'row',
    marginRight: 10
  },
  username: {
    top: -3,
    left: 0,
    fontSize: 12,
    backgroundColor: 'transparent',
    color: Color.timeTextColor
  },
  usernameView: {
    flexDirection: 'row',
    marginHorizontal: 10
  }
};

Bubble.defaultProps = {
  touchableProps: {},
  onLongPress: null,
  renderMessageImage: null,
  renderMessageVideo: null,
  renderMessageText: null,
  renderCustomView: null,
  renderUsername: null,
  renderTicks: null,
  renderTime: null,
  hideFooterMetadata: false,
  position: 'left',
  currentMessage: {
    text: null,
    createdAt: null,
    image: null
  },
  nextMessage: {},
  previousMessage: {},
  containerStyle: {},
  wrapperStyle: {},
  bottomContainerStyle: {},
  tickStyle: {},
  usernameStyle: {},
  containerToNextStyle: {},
  containerToPreviousStyle: {}
};

Bubble.propTypes = {
  user: PropTypes.object.isRequired,
  touchableProps: PropTypes.object,
  onLongPress: PropTypes.func,
  renderMessageImage: PropTypes.func,
  renderMessageVideo: PropTypes.func,
  renderMessageText: PropTypes.func,
  renderCustomView: PropTypes.func,
  renderBubble: PropTypes.func,
  renderUsernameOnMessage: PropTypes.bool,
  renderUsername: PropTypes.func,
  renderTime: PropTypes.func,
  hideFooterMetadata: PropTypes.bool,
  renderTicks: PropTypes.func,
  position: PropTypes.oneOf(['left', 'right']),
  currentMessage: PropTypes.object,
  nextMessage: PropTypes.object,
  previousMessage: PropTypes.object
};
